/**
 * Inline SVG icon set.
 *
 * Icons are decorative by default (`aria-hidden`); when a control has no
 * text label, pass `title` so the icon itself is announced.
 */
import type { JSX } from "preact";

/**
Attributes shared by every icon.
 */
interface IconProperties {
  /**
  24-based pixel size; defaults to 20.
   */
  readonly size?: number;
  /**
  Accessible title; omit for purely decorative use.
   */
  readonly title?: string;
}

/**
 * Builds an `<svg>` with the shared presentation attributes.
 *
 * @param icon - Icon sizing and labelling.
 * @param path - The glyph's path elements.
 * @returns The complete svg element.
 */
function svgIcon(icon: IconProperties, path: JSX.Element): JSX.Element {
  const { size = 20, title } = icon;
  const isLabelled = title !== undefined;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="1.8"
      stroke-linecap="round"
      stroke-linejoin="round"
      role={isLabelled ? "img" : undefined}
      aria-hidden={isLabelled ? undefined : true}
    >
      {isLabelled ? <title>{title}</title> : null}
      {path}
    </svg>
  );
}

/**
 * Envelope (mail) glyph.
 *
 * @param icon - Sizing and labelling.
 * @returns The icon.
 */
export function MailIcon(icon: IconProperties): JSX.Element {
  return svgIcon(icon, <rect x="3" y="5" width="18" height="14" rx="2" />);
}

/**
 * Folder glyph.
 *
 * @param icon - Sizing and labelling.
 * @returns The icon.
 */
export function FolderIcon(icon: IconProperties): JSX.Element {
  return svgIcon(
    icon,
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />,
  );
}

/**
 * Gear (settings) glyph.
 *
 * @param icon - Sizing and labelling.
 * @returns The icon.
 */
export function SettingsIcon(icon: IconProperties): JSX.Element {
  return svgIcon(icon, <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />);
}

/**
 * Paper-plane (compose) glyph.
 *
 * @param icon - Sizing and labelling.
 * @returns The icon.
 */
export function ComposeIcon(icon: IconProperties): JSX.Element {
  return svgIcon(icon, <path d="M22 2 11 13" />);
}

/**
 * Close glyph.
 *
 * @param icon - Sizing and labelling.
 * @returns The icon.
 */
export function CloseIcon(icon: IconProperties): JSX.Element {
  return svgIcon(icon, <path d="M18 6 6 18" />);
}

/**
 * Left chevron (back) glyph.
 *
 * @param icon - Sizing and labelling.
 * @returns The icon.
 */
export function BackIcon(icon: IconProperties): JSX.Element {
  return svgIcon(icon, <path d="m15 18-6-6 6-6" />);
}

/**
 * Flag glyph — filled when the message is flagged.
 *
 * @param icon - Sizing, labelling, and the filled variant.
 * @returns The icon.
 */
export function FlagIcon(
  icon: IconProperties & { readonly filled?: boolean },
): JSX.Element {
  return svgIcon(
    icon,
    <path
      d="M5 21V4m0 0h12l-3 4 3 4H5"
      fill={icon.filled === true ? "currentColor" : "none"}
    />,
  );
}

/**
 * Magnifier (search) glyph.
 *
 * @param icon - Sizing and labelling.
 * @returns The icon.
 */
export function SearchIcon(icon: IconProperties): JSX.Element {
  return svgIcon(
    icon,
    <path d="m21 21-4.35-4.35M17 10.5a6.5 6.5 0 1 1-13 0 6.5 6.5 0 0 1 13 0Z" />,
  );
}
